// DESIGN_AUDIT Phase 11A fixes applied:
//   FIX 1: Replaced 2 occurrences of var(--bg-card) with var(--bg-elevated) — --bg-card was undefined
import { NavLink } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  CheckSquare,
  Calendar,
  DollarSign,
  Settings,
  LogOut,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  FolderKanban,
  ChevronDown,
  BarChart2,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useFacility } from '../../contexts/FacilityContext.tsx';
import { api } from '../../api/client.ts';
import { spinIconOut } from '../../utils/iconAnimations.ts';
import './Sidebar.css';
import '../../styles/buttonAnimations.css';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  // Same boolean-flag-per-item pattern as Settings.tsx's tabs array (adminOnly/viewerHidden/etc)
  // -- hides this item below Manager (Manager, Org Admin, Admin all pass). Projects is currently
  // the only item using it; matches App.tsx's requiredRoles guard on the /projects route.
  managerOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',  label: 'Dashboard',  icon: <LayoutDashboard size={18} className="btn-icon" /> },
  { to: '/inventory',  label: 'Inventory',  icon: <Package size={18} className="btn-icon" /> },
  { to: '/vendors',    label: 'Vendors',    icon: <Users size={18} className="btn-icon" /> },
  { to: '/orders',     label: 'Orders',     icon: <ShoppingCart size={18} className="btn-icon" /> },
  { to: '/tasks',      label: 'Tasks',      icon: <CheckSquare size={18} className="btn-icon" /> },
  { to: '/schedule',   label: 'Calendar',   icon: <Calendar size={18} className="btn-icon" /> },
  { to: '/financials', label: 'Financials', icon: <DollarSign size={18} className="btn-icon" /> },
  { to: '/projects',   label: 'Projects',   icon: <FolderKanban size={18} className="btn-icon" />, managerOnly: true },
  { to: '/reports',    label: 'Reports',    icon: <BarChart2 size={18} className="btn-icon" /> },
  { to: '/learning-hub', label: 'Learning Hub', icon: <BookOpen size={18} className="btn-icon" /> },
  { to: '/settings',   label: 'Settings',   icon: <Settings size={18} className="btn-icon" /> },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface Facility {
  id: number;
  name: string;
  org_id?: number;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
}

// ProQure 2×2 grid logomark — top-left & bottom-right full white, top-right & bottom-left dimmed
function ProQureIcon() {
  // opacity: top-left=1, top-right=0.4, bottom-left=0.4, bottom-right=1
  const opacities = [1, 0.4, 0.4, 1];
  return (
    <div style={{
      width: 36, height: 36,
      background: '#3b82f6',
      borderRadius: 8,
      display: 'grid',
      gridTemplateColumns: '8px 8px',
      gridTemplateRows: '8px 8px',
      gap: 2,
      padding: 4,
      flexShrink: 0,
      alignContent: 'center',
      justifyContent: 'center',
    }}>
      {opacities.map((op, i) => (
        <div key={i} style={{ background: '#fff', borderRadius: 2, opacity: op }} />
      ))}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { user, logout, activeOrgId, activeOrgName, switchOrg } = useAuth();
  const { facilityId, facilityName, setFacility } = useFacility();
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const facilityDropdownRef = useRef<HTMLDivElement>(null);

  // Close org dropdown on outside click
  useEffect(() => {
    if (!orgOpen) return;
    function handleOutsideClick(e: MouseEvent) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setOrgOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [orgOpen]);

  // Close facility dropdown on outside click
  useEffect(() => {
    if (!facilityOpen) return;
    function handleOutsideClick(e: MouseEvent) {
      if (facilityDropdownRef.current && !facilityDropdownRef.current.contains(e.target as Node)) {
        setFacilityOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [facilityOpen]);

  const isAdmin = user?.role === 'admin';
  const canSwitch = user?.role === 'admin' || user?.role === 'org_admin';
  const isManagerOrAbove = ['manager', 'org_admin', 'admin'].includes(user?.role ?? '');

  // Fetch all orgs — admin only
  const { data: orgsRes } = useQuery({
    queryKey: ['organizations-list'],
    queryFn: () => api.get<Organization[]>('/organizations'),
    enabled: isAdmin,
  });
  const orgs: Organization[] = (orgsRes as unknown as { data: Organization[] })?.data ?? [];

  // Fetch all facilities for facility picker
  const { data: facilitiesRes } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: () => api.get<Facility[]>('/facilities'),
    enabled: canSwitch,
  });
  const allFacilities: Facility[] = (facilitiesRes as unknown as { data: Facility[] })?.data ?? [];

  // Filter facilities to those matching active org_id
  const facilities: Facility[] = allFacilities.filter((f) => {
    if (!f.org_id) return true; // no org_id yet (pre-migration) — show all
    return f.org_id === activeOrgId;
  });

  // Current org display name
  const displayOrgName = activeOrgName || user?.orgName || '';

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* ── Logo / Brand ────────────────────────────────────────────────── */}
      <div className="sidebar-brand">
        {collapsed ? (
          <div className="sidebar-logo">
            <ProQureIcon />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            {/* Logo row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ProQureIcon />
              <div className="sidebar-brand-text">
                <div style={{ fontSize: 16, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#f9fafb', fontWeight: 700 }}>Pro</span><span style={{ color: '#3b82f6', fontWeight: 700 }}>Qure</span>
                </div>
                <span style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  whiteSpace: 'nowrap',
                }}>
                  Facility Management
                </span>
              </div>
            </div>

            {/* Org name row */}
            {isAdmin ? (
              /* Admin: clickable org dropdown */
              <div ref={orgDropdownRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setOrgOpen((o) => !o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', padding: '5px 8px',
                    borderRadius: 'var(--radius-sm)',
                    background: orgOpen ? 'var(--bg-hover)' : 'rgba(59,130,246,0.08)',
                    border: '1px solid rgba(59,130,246,0.2)',
                    color: 'var(--accent-primary)', cursor: 'pointer',
                    fontSize: '0.75rem', fontWeight: 600,
                    transition: 'all var(--transition)',
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayOrgName || 'Select Org'}
                  </span>
                  <ChevronDown size={12} />
                </button>
                {orgOpen && orgs.length > 0 && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
                    zIndex: 300, overflow: 'hidden',
                  }}>
                    {orgs.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => {
                          switchOrg(o.id, o.name);
                          setOrgOpen(false);
                        }}
                        style={{
                          display: 'block', width: '100%', padding: '8px 12px',
                          background: o.id === activeOrgId ? 'rgba(59,130,246,0.1)' : 'none',
                          border: 'none',
                          color: o.id === activeOrgId ? 'var(--accent-primary)' : 'var(--text-primary)',
                          cursor: 'pointer', fontSize: '0.8rem',
                          textAlign: 'left', fontWeight: o.id === activeOrgId ? 600 : 400,
                        }}
                      >
                        {o.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Non-admin: static org name label */
              <span style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                paddingLeft: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayOrgName}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Navigation ──────────────────────────────────────────────────── */}
      <nav className="sidebar-nav">
        {NAV_ITEMS.filter((item) => !item.managerOnly || isManagerOrAbove).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? 'active' : ''}`
            }
            title={collapsed ? item.label : undefined}
            onClick={onMobileClose}
            onMouseLeave={spinIconOut}
          >
            <span className="sidebar-nav-icon">{item.icon}</span>
            {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* ── Footer: facility switcher + logout ──────────────────────────── */}
      <div className="sidebar-footer">
        {/* Facility Switcher (admin/org_admin only, expanded state) */}
        {!collapsed && canSwitch && (
          <div ref={facilityDropdownRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setFacilityOpen((o) => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 10px',
                borderRadius: 'var(--radius-md)',
                background: facilityOpen ? 'var(--bg-hover)' : 'none',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)', cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 500,
                transition: 'all var(--transition)',
              }}
            >
              <Building2 size={14} style={{ flexShrink: 0, color: 'var(--accent-primary)' }} />
              <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {facilityName || 'Select Facility'}
              </span>
              <ChevronsUpDown size={12} />
            </button>
            {facilityOpen && facilities.length > 0 && (
              <div style={{
                // maxHeight + overflowY here is required — .sidebar itself is overflow:hidden
                // (Sidebar.css), so without its own scroll container this dropdown just got
                // silently clipped by the sidebar's bounds past whatever fit (~5 items), with
                // the rest completely unreachable rather than scrollable. facilities.map() below
                // already renders every facility — this was never a hardcoded data limit.
                position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4,
                background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
                zIndex: 200, overflow: 'hidden',
                maxHeight: 280, overflowY: 'auto',
                scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent',
              }}>
                {facilities.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setFacility(f.id, f.name);
                      setFacilityOpen(false);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 12px',
                      background: f.id === facilityId ? 'rgba(59,130,246,0.1)' : 'none',
                      border: 'none',
                      color: f.id === facilityId ? 'var(--accent-primary)' : 'var(--text-primary)',
                      cursor: 'pointer', fontSize: '0.8125rem',
                      textAlign: 'left',
                    }}
                  >
                    <Building2 size={13} />
                    {f.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="sidebar-footer-row">
          <button
            className="sidebar-collapse-btn"
            onClick={onToggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onMouseLeave={spinIconOut}
          >
            {collapsed ? <ChevronRight size={16} className="btn-icon" /> : <ChevronLeft size={16} className="btn-icon" />}
          </button>

          <button
            className="sidebar-logout-btn"
            onClick={logout}
            title="Logout"
            onMouseLeave={spinIconOut}
          >
            <LogOut size={16} className="btn-icon" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}
